import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, ChevronRight, CheckCircle2, Zap } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { useIndustries, useIndustryBySlug, usePageContent } from "@/lib/content";
import { StarMotif } from "@/components/common/StarMotif";
import { CTABlock } from "@/components/common/CTABlock";
import { absUrl } from "@/components/admin/UploadWidget";

// Per-industry CTA config — all 7 slugs defined below
const INDUSTRY_CTAS = {
    retail: {
        headline: "Modernize Your Retail Architecture & Scale Omnichannel Commerce",
        body: "Partner with RKS Systems engineers to build high-throughput e-commerce engines, real-time inventory synchronization systems, and POS integrations designed for peak holiday loads and frictionless checkout experiences.",
        primaryLabel: "Schedule a Retail Tech Session",
        primaryTo: "/schedule-meeting",
        primarySub: "30 mins with a Principal Retail Systems Architect.",
        secondaryLabel: "Request an Omnichannel Quote",
        secondaryTo: "/request-quote",
        secondarySub: "Detailed scope and timeline within 24 business hours.",
        anchor: { label: "Looking to streamline invoice processing? See InvoiceOS for Retail →", to: "/products/prod-invoiceos" },
    },
    bfsi: {
        headline: "Build Secure, Compliant, & Scalable Financial Technology",
        body: "Modernize legacy core banking platforms, integrate fraud detection AI engines, and secure payment workflows with enterprise DevSecOps that strictly align with PCI-DSS, SOC2, and global banking compliance standards.",
        primaryLabel: "Consult a BFSI Solutions Architect",
        primaryTo: "/schedule-meeting",
        primarySub: "Direct access to senior fintech engineers.",
        secondaryLabel: "Get a Fixed-Price BFSI Estimate",
        secondaryTo: "/request-quote",
        secondarySub: "Secure project scoping with transparent pricing models.",
        anchor: { label: "Automating financial workflows? Explore Accounts Payable AI Automation →", to: "/products/prod-invoiceos" },
    },
    logistics: {
        headline: "Optimize Fleet Operations & Real-Time Supply Chain Visibility",
        body: "Engineer scalable logistics platforms, route optimization AI, and connected IoT telematics that reduce delivery transit times, automate inventory tracking, and lower operational overhead.",
        primaryLabel: "Schedule a Supply Chain Tech Audit",
        primaryTo: "/schedule-meeting",
        primarySub: "Discuss system architecture with a Principal Logistics Engineer.",
        secondaryLabel: "Request a Logistics Solution Quote",
        secondaryTo: "/request-quote",
        secondarySub: "Tailored scope and delivery roadmap within 24 hours.",
        anchor: { label: "Automating freight vendor billing? Discover InvoiceOS Logistics Engine →", to: "/products/prod-invoiceos" },
    },
    "food-and-beverage": {
        headline: "Drive Multi-Unit Operational Efficiency & Inventory Control",
        body: "Deploy intelligent analytics, automated kitchen execution systems (KDS), and demand forecasting engines to minimize inventory waste, control food costs, and scale multi-location restaurant operations.",
        primaryLabel: "Schedule an F&B Operations Consultation",
        primaryTo: "/schedule-meeting",
        primarySub: "Meet directly with an RKS Systems Engineering Director.",
        secondaryLabel: "Get a Custom Development Estimate",
        secondaryTo: "/request-quote",
        secondarySub: "Transparent T&M or fixed-cost breakdown.",
        anchor: { label: "Managing complex supplier invoices? See Automated AP Workflows →", to: "/products/prod-invoiceos" },
    },
    hospitality: {
        headline: "Transform Guest Experiences with Smart Hospitality Software",
        body: "Engineer unified property management systems (PMS), contactless check-in platforms, and AI-driven guest personalization engines that increase RevPAR and streamline hotel management.",
        primaryLabel: "Talk to a Hospitality Systems Engineer",
        primaryTo: "/schedule-meeting",
        primarySub: "30-minute architectural and integration review.",
        secondaryLabel: "Request a Fixed-Price Quote",
        secondaryTo: "/request-quote",
        secondarySub: "Receive a complete proposal within 24 business hours.",
        anchor: { label: "Centralizing hotel vendor payouts? Explore InvoiceOS for Hospitality →", to: "/products/prod-invoiceos" },
    },
    "energy-and-utilities": {
        headline: "Accelerate Smart Grid Innovation & Predictive Maintenance",
        body: "Modernize utility infrastructure with real-time SCADA analytics, AI-driven predictive fault detection, and resilient cloud architectures engineered for continuous operational uptime.",
        primaryLabel: "Schedule an Energy Tech Consultation",
        primaryTo: "/schedule-meeting",
        primarySub: "Speak with a Principal Cloud & Data Engineer.",
        secondaryLabel: "Request an Enterprise Project Proposal",
        secondaryTo: "/request-quote",
        secondarySub: "Complete technical scope, risk evaluation, and pricing.",
        anchor: { label: "Modernizing enterprise asset data? Explore Our Data & AI Practice →", to: "/what-we-do/data-ai" },
    },
    manufacturing: {
        headline: "Build Connected Industry 4.0 & Smart Factory Platforms",
        body: "Connect production lines with IoT edge computing, computer vision quality inspection, and automated ERP integrations that reduce downtime and boost overall equipment effectiveness (OEE).",
        primaryLabel: "Schedule a Smart Factory Tech Session",
        primaryTo: "/schedule-meeting",
        primarySub: "Discuss manufacturing automation with a Lead Principal Engineer.",
        secondaryLabel: "Request a Fixed-Price / T&M Quote",
        secondaryTo: "/request-quote",
        secondarySub: "Written delivery roadmap within 24 business hours.",
        anchor: { label: "Need automated quality verification? Explore Quality Engineering →", to: "/what-we-do/testing" },
    },
};

function IndustryCTABlock({ slug }) {
    const pc = usePageContent(`industry-${slug}`);
    const def = INDUSTRY_CTAS[slug];
    const cta = (pc && pc.footer_headline) ? {
        headline: pc.footer_headline, body: pc.footer_body,
        primaryLabel: pc.footer_primary_label, primaryTo: pc.footer_primary_url || "/schedule-meeting", primarySub: pc.footer_primary_sub,
        secondaryLabel: pc.footer_secondary_label, secondaryTo: pc.footer_secondary_url || "/request-quote", secondarySub: pc.footer_secondary_sub,
        anchor: (pc.footer_anchor_label && pc.footer_anchor_url) ? { label: pc.footer_anchor_label, to: pc.footer_anchor_url } : null,
    } : def;
    if (!cta) return <CTABlock />;
    return (
        <section className="relative overflow-hidden bg-[hsl(222_47%_7%)] text-white" data-testid={`industry-cta-${slug}`}>
            <div className="absolute -right-16 -top-16 h-96 w-96 opacity-10">
                <StarMotif className="h-full w-full text-primary" label="RKS" labelColor="currentColor" />
            </div>
            <div className="absolute inset-0 grid-lines opacity-40" />
            <div className="container-app relative py-20 lg:py-24">
                <span className="overline text-primary/90">Next step</span>
                <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.05] max-w-3xl">
                    {cta.headline}
                </h2>
                <p className="mt-5 text-base text-white/70 leading-relaxed max-w-2xl">
                    {cta.body}
                </p>
                <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl">
                    <div className="rounded-xl border border-primary/40 bg-white/[0.03] p-5 flex flex-col gap-2.5">
                        <Link to={cta.primaryTo} data-testid={`industry-cta-${slug}-primary`}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white hover:bg-primary/90 transition-colors text-sm text-center">
                            {cta.primaryLabel} <ArrowRight className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
                        </Link>
                        <p className="text-xs text-white/50 text-center">{cta.primarySub}</p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 flex flex-col gap-2.5">
                        <Link to={cta.secondaryTo} data-testid={`industry-cta-${slug}-secondary`}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 bg-white/5 px-5 py-3 font-semibold text-white hover:bg-white/10 hover:border-white/40 transition-colors text-sm text-center">
                            {cta.secondaryLabel} <ArrowRight className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
                        </Link>
                        <p className="text-xs text-white/50 text-center">{cta.secondarySub}</p>
                    </div>
                </div>
                {cta.anchor && (
                    <div className="mt-8">
                        <Link to={cta.anchor.to} data-testid={`industry-cta-${slug}-anchor`}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
                            <Zap className="h-3.5 w-3.5 flex-shrink-0" />
                            {cta.anchor.label}
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}

export function SolutionsIndex() {
    const { items: industries } = useIndustries();
    useSEO({
        title: "Solutions by Industry | RKS Systems",
        description: "Deep patterns and reference architectures for retail, BFSI, logistics, F&B, hospitality, energy & utilities, manufacturing, startups and consulting.",
    });
    return (
        <div data-testid="page-solutions-index">
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-24 lg:py-32">
                    <div className="max-w-3xl">
                        <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]">
                            Industry-shaped outcomes.
                        </h1>
                        <p className="mt-6 text-lg text-white/70 leading-relaxed">
                            Reference architectures, playbooks, and delivery leaders who have shipped in your industry — from BFSI cores to smart factories.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28">
                <div className="container-app">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {industries.map((i) => (
                            <Link key={i.slug} to={`/solutions/${i.slug}`} data-testid={`industry-card-${i.slug}`}
                                className="group relative overflow-hidden rounded-xl border border-border bg-white transition-all hover:border-primary/40 hover:shadow-lg hover:-translate-y-1">
                                <div className="aspect-[16/10] overflow-hidden">
                                    {i.image && <img src={absUrl(i.image)} alt={i.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                                </div>
                                <div className="p-6">
                                    <h3 className="font-heading text-xl font-bold">{i.name}</h3>
                                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{i.headline}</p>
                                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">Explore <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} /></div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
            <CTABlock />
        </div>
    );
}

export function IndustryDetail() {
    const { slug } = useParams();
    const { item: i, notFound } = useIndustryBySlug(slug);
    useSEO({
        title: i ? `${i.name} Solutions — RKS Systems` : "Industry — RKS Systems",
        description: i?.headline,
    });
    if (notFound) return <Navigate to="/solutions" replace />;
    if (!i) return <div className="container-app py-24 text-center text-muted-foreground">Loading...</div>;
    return (
        <div data-testid={`page-industry-${i.slug}`}>
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-16 lg:py-24">
                    <Link to="/solutions" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8"><ArrowLeft className="h-3.5 w-3.5" /> All industries</Link>
                    <div className="grid lg:grid-cols-[1.6fr,1fr] gap-12 items-center">
                        <div>
                            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">{i.headline || i.name}</h1>
                            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-2xl">{i.value_prop}</p>
                            <div className="mt-9 flex flex-wrap gap-3">
                                <Link to="/schedule-meeting" className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white btn-lift hover:bg-primary/90">Schedule Meeting <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} /></Link>
                                <Link to="/request-quote" className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/[0.04] px-5 py-3 font-semibold text-white btn-lift hover:bg-white/[0.08]">Request Quote</Link>
                            </div>
                        </div>
                        {i.image && (
                            <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10">
                                <img src={absUrl(i.image)} alt={i.name} className="h-full w-full object-cover" />
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <div className="container-app grid lg:grid-cols-2 gap-12">
                    {i.challenges?.length ? (
                        <div>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold">Industry challenges</h2>
                            <ul className="mt-5 space-y-3">
                                {i.challenges.map((c) => (
                                    <li key={c} className="flex items-start gap-3 text-base text-foreground/85">
                                        <ChevronRight className="h-4 w-4 mt-1.5 text-primary flex-shrink-0" strokeWidth={2} />
                                        <span>{c}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    {i.offerings?.length ? (
                        <div>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold">Solution offerings</h2>
                            <div className="mt-5 grid gap-3">
                                {i.offerings.map((o) => (
                                    <div key={o} className="rounded-lg border border-border bg-card px-4 py-3 flex items-start gap-3">
                                        <StarMotif className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                                        <span className="text-sm">{o}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </section>

            <section className="py-16 lg:py-20 bg-secondary/40">
                <div className="container-app grid lg:grid-cols-2 gap-12">
                    {i.outcomes?.length ? (
                        <div>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold">Business outcomes</h2>
                            <ul className="mt-5 space-y-3">
                                {i.outcomes.map((o) => (
                                    <li key={o} className="flex items-start gap-3 text-base text-foreground/85">
                                        <CheckCircle2 className="h-4 w-4 mt-1 text-primary flex-shrink-0" strokeWidth={1.75} />
                                        <span>{o}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    {i.recommended_services?.length ? (
                        <div>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold">Recommended services</h2>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {i.recommended_services.map((r) => (
                                    <span key={r} className="inline-flex items-center rounded-md border border-primary/20 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary">{r}</span>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </section>
            <IndustryCTABlock slug={i.slug} />
        </div>
    );
}
